import React from "react";
import { Typography, ProgressBar, Avatar, Flex, Card, Pill, Tooltip } from "@procore/core-react";
import { getAttachedDate } from "@laborchart-modules/common/dist/datetime/date";
import { formatDateTime } from "@/react/shared/helper";
import { useI18nContext } from "@procore/core-react";
import { formatNumberWithPercentage } from "@/react/components/boards/boards-pure-utils";
import { useProfilePicture } from "@/react/shared/hooks/use-profile-picture";
import { ProcoreStore } from "@/stores/procore-store.core";

interface ResourceCardProps {
   resource: any;
   showJobTitles?: boolean;
   showTags?: boolean;
   draggedResourceRef: React.MutableRefObject<string | null>;
   lastDraggedResourceRef: React.MutableRefObject<string | null>;
   isSelected?: boolean;
   onSelectionChange?: (resourceId: string, isSelected: boolean) => void;
   selectedResources?: any[];
   rank?: number;
}

export const TooltipContent = ({
   availability,
   proximity,
   jobTitleMatch,
   tagsMatch,
   projectHasLatLong,
}: {
   availability: number;
   proximity?: number | null;
   jobTitleMatch: number;
   tagsMatch: number;
   projectHasLatLong?: boolean;
}) => {
   const I18n = useI18nContext();
   const availabilityPercentage = formatNumberWithPercentage(availability, I18n.locale);
   const jobTitleMatchPercentage = formatNumberWithPercentage(jobTitleMatch * 100, I18n.locale);
   const tagsMatchPercentage = formatNumberWithPercentage(tagsMatch * 100, I18n.locale);
   let proximityDistance;
   if (!projectHasLatLong) {
      proximityDistance = I18n.t(
         "views.company.workforce_planning.smart_bench.tooltip.proximity_not_available_project_missing_address",
      );
   } else if (!proximity && proximity !== 0) {
      proximityDistance = I18n.t(
         "views.company.workforce_planning.smart_bench.tooltip.proximity_not_available_resource_missing_address",
      );
   } else {
      proximityDistance = I18n.t("views.company.workforce_planning.smart_bench.tooltip.proximity", {
         distance: Math.round(proximity),
      });
   }
   return (
      <Tooltip.Content>
         <div style={{ marginBottom: "4px" }}>
            {I18n.t("views.company.workforce_planning.smart_bench.tooltip.suggestion_ranking")}
         </div>
         <ul style={{ listStyleType: "disc", paddingLeft: "20px", margin: 0 }}>
            <li style={{ display: "list-item" }}>
               {I18n.t("views.company.workforce_planning.smart_bench.tooltip.availability", {
                  percent: availabilityPercentage,
               })}
            </li>
            <li style={{ display: "list-item" }}>{proximityDistance}</li>
            <li style={{ display: "list-item" }}>
               {I18n.t("views.company.workforce_planning.smart_bench.tooltip.job_title_match", {
                  percent: jobTitleMatchPercentage,
               })}
            </li>
            <li style={{ display: "list-item" }}>
               {I18n.t("views.company.workforce_planning.smart_bench.tooltip.tags_match", {
                  percent: tagsMatchPercentage,
               })}
            </li>
         </ul>
      </Tooltip.Content>
   );
};

export const ResourceCard: React.FC<ResourceCardProps> = ({
   resource,
   showJobTitles = true,
   showTags = true,
   draggedResourceRef,
   lastDraggedResourceRef,
   isSelected = false,
   onSelectionChange,
   selectedResources = [],
   rank,
}) => {
   const I18n = useI18nContext();
   const resolvedPhotoUrl = useProfilePicture(resource.resource_photo_url);

   // Touch drag state
   const touchStartRef = React.useRef<{ x: number; y: number } | null>(null);
   const cardRef = React.useRef<HTMLDivElement>(null);
   const dragGhostRef = React.useRef<HTMLDivElement | null>(null);
   const lastUpdateRef = React.useRef<number>(0);
   const dragThreshold = 10; // Minimum distance to start drag

   // Calculate availability text
   const availabilityText = React.useMemo(() => {
      if (resource.next_assignments && resource.next_assignments.length > 0) {
         const nextAssignmentDate = getAttachedDate(resource.next_assignments[0].start_day);
         const formattedDate = formatDateTime(nextAssignmentDate, { locale: I18n.locale });

         return I18n.t("views.company.workforce_planning.bench.availability_until", {
            date: formattedDate,
            percent: resource.available_percentage,
         });
      }

      if (resource.available_percentage === 100) {
         return I18n.t("views.company.workforce_planning.bench.available");
      }

      return I18n.t("views.company.workforce_planning.bench.partially_available", {
         percent: resource.available_percentage,
      });
   }, [resource, I18n]);

   // Helper function to create drag data (shared between drag and touch)
   const createDragData = () => {
      // Determine resources to drag - either selected resources or just this one
      const resourcesToDrag =
         isSelected && selectedResources.length > 0 ? selectedResources : [resource];

      const createResourceData = (res: any) => ({
         resource_id: res.resource_id,
         resource_title: res.resource_title,
         position_name: res.position_name,
         resource_color: res.resource_color,
         employee_number: res.employee_number,
         resource_hourly_wage: res.resource_rate,
         job_title_rate: res.position_rate,
         resource_tags:
            res.resource_tag_instances?.map((ti: any) => ({
               color: ti.color,
               name: ti.name,
               abbreviation: ti.abbreviation,
               require_expr_date: ti.require_expr_date,
               expr_days_warning: ti.expr_days_warning,
               expr_date: ti.expr_date,
            })) || [],
         resource_address_1: res.baggage?.resource_address_1,
         resource_address_2: res.baggage?.resource_address_2,
         resource_city_town: res.baggage?.resource_city_town,
         resource_state_province: res.baggage?.resource_state_province,
         resource_zipcode: res.baggage?.resource_zipcode,
         is_smart_bench: !!rank,
         tags_match_ratio: res.tags_match_ratio,
         job_title_match_status_score: res.job_title_match_status_score,
         proximity: res.proximity,
      });

      return {
         type: resourcesToDrag.length > 1 ? "resources" : "resource",
         data:
            resourcesToDrag.length > 1
               ? resourcesToDrag.map(createResourceData)
               : createResourceData(resourcesToDrag[0]),
      };
   };

   // Helper function to add visual feedback (shared between drag and touch)
   const addDragVisualFeedback = (currentTarget: HTMLElement, resourcesToDrag: any[]) => {
      // Store the resource ID in our refs
      draggedResourceRef.current = resource.resource_id;
      lastDraggedResourceRef.current = resource.resource_id;

      // Add a class to show this is being dragged
      currentTarget.classList.add("dragging");

      // Add visual feedback for multiple items
      if (resourcesToDrag.length > 1) {
         currentTarget.classList.add("dragging-multiple");

         // Add dragging-source class to all selected cards
         resourcesToDrag.forEach((res) => {
            const cardElement = document.querySelector(`[data-resource-id="${res.resource_id}"]`);
            if (cardElement) {
               cardElement.classList.add("dragging-source");
            }
         });
      }
   };

   // Helper function to remove visual feedback (shared between drag and touch)
   const removeDragVisualFeedback = (currentTarget: HTMLElement) => {
      // Remove the dragging classes
      currentTarget.classList.remove("dragging");
      currentTarget.classList.remove("dragging-multiple");

      // Remove dragging-source class from all selected cards
      const draggingSourceCards = document.querySelectorAll(".dragging-source");
      draggingSourceCards.forEach((card) => {
         card.classList.remove("dragging-source");
      });

      // Clear the dragged resource reference after a short delay
      // to ensure the drop handler has time to process
      setTimeout(() => {
         draggedResourceRef.current = null;
      }, 100);
   };

   // Helper function to create drag ghost for touch
   const createDragGhost = (
      sourceElement: HTMLElement,
      resourcesToDrag: any[],
      initialX: number,
      initialY: number,
   ) => {
      const ghost = sourceElement.cloneNode(true) as HTMLDivElement;

      // Position ghost at touch location initially
      const offsetX = sourceElement.offsetWidth / 2;
      const offsetY = sourceElement.offsetHeight / 2;

      // Style the ghost
      ghost.style.position = "fixed";
      ghost.style.top = initialY - offsetY + "px";
      ghost.style.left = initialX - offsetX + "px";
      ghost.style.width = sourceElement.offsetWidth + "px";
      ghost.style.height = sourceElement.offsetHeight + "px";
      ghost.style.pointerEvents = "none";
      ghost.style.zIndex = "9999";
      ghost.style.opacity = "0";
      ghost.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.2)";
      ghost.style.borderRadius = "8px";
      ghost.style.transition = "opacity 0.15s ease, transform 0.15s ease";
      ghost.classList.add("touch-drag-ghost");

      // For multiple items, add a badge showing count
      if (resourcesToDrag.length > 1) {
         const badge = document.createElement("div");
         badge.textContent = resourcesToDrag.length.toString();
         badge.style.position = "absolute";
         badge.style.top = "-8px";
         badge.style.right = "-8px";
         badge.style.backgroundColor = "#0066cc";
         badge.style.color = "white";
         badge.style.borderRadius = "50%";
         badge.style.width = "24px";
         badge.style.height = "24px";
         badge.style.display = "flex";
         badge.style.alignItems = "center";
         badge.style.justifyContent = "center";
         badge.style.fontSize = "12px";
         badge.style.fontWeight = "bold";
         badge.style.border = "2px solid white";
         badge.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.2)";
         ghost.appendChild(badge);
      }

      document.body.appendChild(ghost);

      // Animate in smoothly and immediately
      requestAnimationFrame(() => {
         ghost.style.opacity = "0.85";
         ghost.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.3)";
      });

      return ghost;
   };

   // Helper function to update drag ghost position (throttled for smoothness)
   const updateDragGhostPosition = (ghost: HTMLDivElement, x: number, y: number) => {
      const now = Date.now();
      if (now - lastUpdateRef.current < 16) return; // Throttle to ~60fps
      lastUpdateRef.current = now;

      // Smooth position update
      const offsetX = ghost.offsetWidth / 2;
      const offsetY = ghost.offsetHeight / 2;

      // Remove transition temporarily for position updates to avoid lag
      const hasTransition = ghost.style.transition;
      ghost.style.transition = "none";
      ghost.style.left = x - offsetX + "px";
      ghost.style.top = y - offsetY + "px";

      // Restore transition after position update
      if (hasTransition) {
         requestAnimationFrame(() => {
            ghost.style.transition = hasTransition;
         });
      }
   };

   // Helper function to remove drag ghost with smooth exit animation
   const removeDragGhost = () => {
      if (dragGhostRef.current && document.body.contains(dragGhostRef.current)) {
         const ghost = dragGhostRef.current;

         // Animate out smoothly
         ghost.style.transition = "opacity 0.2s ease, transform 0.2s ease";
         ghost.style.opacity = "0";
         ghost.style.transform = "rotate(0deg) scale(0.8)";

         // Remove from DOM after animation
         setTimeout(() => {
            if (document.body.contains(ghost)) {
               document.body.removeChild(ghost);
            }
         }, 200);

         dragGhostRef.current = null;
      }
   };

   // Handle click for selection
   const handleClick = (e: React.MouseEvent) => {
      // Only handle selection if onSelectionChange is provided and cmd/ctrl is pressed
      if (onSelectionChange && (e.metaKey || e.ctrlKey)) {
         e.preventDefault();
         onSelectionChange(resource.resource_id, !isSelected);
      }
   };

   // Touch event handlers (non-React handlers to avoid passive listener issues)
   React.useEffect(() => {
      const cardElement = cardRef.current;
      if (!cardElement) return;

      const handleTouchStart = (e: TouchEvent) => {
         const touch = e.touches[0];
         touchStartRef.current = { x: touch.clientX, y: touch.clientY };
      };

      const handleTouchMove = (e: TouchEvent) => {
         if (!touchStartRef.current) return;

         const touch = e.touches[0];
         const deltaX = touch.clientX - touchStartRef.current.x;
         const deltaY = touch.clientY - touchStartRef.current.y;
         const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

         // Check if we've moved enough to start dragging
         if (distance > dragThreshold) {
            // Prevent default touch behavior (scrolling, etc.)
            e.preventDefault();

            // Create drag data and start touch drag if not already started
            if (!cardElement.classList.contains("dragging")) {
               ProcoreStore.logAnalyticsEvent({
                  key: "company.workforce_planning.boards.drag_and_drop_initiated",
                  properties: {},
               });
               const dragData = createDragData();
               const resourcesToDrag =
                  isSelected && selectedResources.length > 0 ? selectedResources : [resource];

               // Add visual feedback
               addDragVisualFeedback(cardElement, resourcesToDrag);

               // Create and show drag ghost
               const ghost = createDragGhost(
                  cardElement,
                  resourcesToDrag,
                  touch.clientX,
                  touch.clientY,
               );
               dragGhostRef.current = ghost;

               // Dispatch touch drag start event
               const touchDragStartEvent = new CustomEvent("resourceTouchDragStart", {
                  detail: { dragData },
               });
               document.dispatchEvent(touchDragStartEvent);
            }
         }

         // Update ghost position if dragging
         if (cardElement.classList.contains("dragging") && dragGhostRef.current) {
            updateDragGhostPosition(dragGhostRef.current, touch.clientX, touch.clientY);
         }
      };

      const handleTouchEnd = () => {
         touchStartRef.current = null;

         // If we were dragging, clean up
         if (cardElement.classList.contains("dragging")) {
            removeDragVisualFeedback(cardElement);
            removeDragGhost();
         }
      };

      const handleTouchCancel = () => {
         touchStartRef.current = null;

         // If we were dragging, clean up and cancel
         if (cardElement.classList.contains("dragging")) {
            removeDragVisualFeedback(cardElement);
            removeDragGhost();

            // Dispatch touch drag cancel event
            const touchDragCancelEvent = new CustomEvent("resourceTouchDragCancel");
            document.dispatchEvent(touchDragCancelEvent);
         }
      };

      // Add non-passive touch event listeners
      cardElement.addEventListener("touchstart", handleTouchStart, { passive: true });
      cardElement.addEventListener("touchmove", handleTouchMove, { passive: false });
      cardElement.addEventListener("touchend", handleTouchEnd, { passive: true });
      cardElement.addEventListener("touchcancel", handleTouchCancel, { passive: true });

      return () => {
         cardElement.removeEventListener("touchstart", handleTouchStart);
         cardElement.removeEventListener("touchmove", handleTouchMove);
         cardElement.removeEventListener("touchend", handleTouchEnd);
         cardElement.removeEventListener("touchcancel", handleTouchCancel);
         // Clean up any remaining drag ghost
         removeDragGhost();
      };
   }, [resource.resource_id, isSelected, selectedResources]);

   return (
      <Tooltip
         overlay={
            <TooltipContent
               availability={resource.available_percentage}
               proximity={resource.proximity}
               jobTitleMatch={resource.job_title_match_status_score}
               tagsMatch={resource.tags_match_ratio}
               projectHasLatLong={resource.project_has_lat_long}
            />
         }
         // Disable the tooltip if the resource is not found via smart bench,
         // aka if there is no percentage match.
         trigger={rank ? "hover" : "none"}
      >
         <Card
            ref={cardRef}
            className={`bench-resource-card ${isSelected ? "selected" : ""}`}
            draggable={true}
            tabIndex={0}
            role="button"
            aria-label={`Assign ${resource.resource_title} to project`}
            data-resource-id={resource.resource_id}
            title={
               isSelected && selectedResources.length > 1
                  ? `${selectedResources.length} resources selected for assignment`
                  : undefined
            }
            onClick={handleClick}
            onDragStart={(e) => {
               const dragData = createDragData();
               const resourcesToDrag =
                  isSelected && selectedResources.length > 0 ? selectedResources : [resource];
               ProcoreStore.logAnalyticsEvent({
                  key: "company.workforce_planning.boards.drag_and_drop_initiated",
                  properties: {},
               });

               // Set both formats to ensure compatibility
               e.dataTransfer.setData("application/json", JSON.stringify(dragData));
               e.dataTransfer.setData("text/plain", JSON.stringify(dragData));

               // Set drag image and effects
               e.dataTransfer.effectAllowed = "copy";

               // Add visual feedback
               addDragVisualFeedback(e.currentTarget, resourcesToDrag);

               // Add visual feedback for multiple items (drag image creation)
               if (resourcesToDrag.length > 1) {
                  // Create a multi-card drag preview
                  const dragContainer = document.createElement("div");
                  dragContainer.className = "multi-card-drag-preview";
                  dragContainer.style.pointerEvents = "none";

                  // Create preview cards (max 4 cards to avoid clutter)
                  const maxCards = Math.min(resourcesToDrag.length, 4);
                  for (let i = 0; i < maxCards; i++) {
                     const cardClone = e.currentTarget.cloneNode(true) as HTMLElement;
                     cardClone.style.position = "absolute";
                     cardClone.style.left = i * 8 + "px"; // Stagger horizontally
                     cardClone.style.top = i * 6 + "px"; // Stagger vertically
                     cardClone.style.transform = `rotate(${i * 2 - 3}deg)`; // Slight rotation for fan effect
                     cardClone.style.zIndex = (maxCards - i).toString();
                     cardClone.style.opacity = i === 0 ? "1" : (0.8 - i * 0.1).toString();
                     cardClone.style.width = e.currentTarget.offsetWidth + "px";
                     cardClone.style.height = e.currentTarget.offsetHeight + "px";
                     cardClone.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.3)";
                     cardClone.classList.add("drag-preview-card");

                     // Update the card content if we have resource data
                     if (i < resourcesToDrag.length) {
                        const nameElement = cardClone.querySelector('[data-testid="person-name"]');
                        if (nameElement) {
                           nameElement.textContent = resourcesToDrag[i].resource_title;
                        }
                     }

                     dragContainer.appendChild(cardClone);
                  }
                  document.body.appendChild(dragContainer);

                  // Use the custom element as drag image (only if supported)
                  if (e.dataTransfer.setDragImage) {
                     e.dataTransfer.setDragImage(dragContainer, 20, 20);
                  }

                  // Clean up the temporary element after a short delay
                  setTimeout(() => {
                     if (document.body.contains(dragContainer)) {
                        document.body.removeChild(dragContainer);
                     }
                  }, 100);
               }
            }}
            onDragEnd={(e) => {
               removeDragVisualFeedback(e.currentTarget);
            }}
            onKeyDown={(e) => {
               // Allow keyboard users to initiate drag with Enter or Space
               if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  // Focus the first project column for keyboard assignment
                  const firstProjectColumn = document.querySelector(
                     ".b-task-board-column-body-inner",
                  );
                  if (firstProjectColumn) {
                     (firstProjectColumn as HTMLElement).focus();
                  }
               }
            }}
            style={{
               display: "flex",
               alignItems: "flex-start",
               marginBottom:
                  showJobTitles || (showTags && resource.resource_tag_instances?.length > 0)
                     ? "8px"
                     : "4px",
               borderLeft: `6px solid ${resource.resource_color || "#ccc"}`,
            }}
         >
            <div style={{ flex: 1, position: "relative" }}>
               <Flex gap="sm">
                  <Avatar>
                     {resolvedPhotoUrl ? (
                        <Avatar.Portrait imageUrl={resolvedPhotoUrl} />
                     ) : (
                        <Avatar.Label>{resource.resource_initials}</Avatar.Label>
                     )}
                  </Avatar>
                  <Flex direction="column" style={{ width: "100%" }}>
                     <Flex direction="row" justifyContent="space-between" style={{ width: "100%" }}>
                        <Typography
                           as="div"
                           data-testid="person-name"
                           weight="semibold"
                           color="gray15"
                           style={{
                              fontSize: 14,
                              marginBottom: showJobTitles
                                 ? 2
                                 : showTags && resource.resource_tag_instances?.length > 0
                                 ? 2
                                 : 0,
                              lineHeight: 1.3,
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                           }}
                        >
                           {resource.resource_title}
                        </Typography>
                        {rank && <Pill>{rank.toString()}</Pill>}
                     </Flex>
                     {showJobTitles && (
                        <Typography
                           as="div"
                           color="gray10"
                           style={{ fontSize: 12, marginBottom: 2, lineHeight: 1.3 }}
                        >
                           {resource.position_name}
                        </Typography>
                     )}
                     <div
                        style={{
                           display: "flex",
                           justifyContent: "space-between",
                           alignItems: "flex-start",
                           width: "100%",
                           gap: "4px",
                        }}
                     >
                        <Typography
                           as="div"
                           color="green10"
                           style={{ fontSize: 11, lineHeight: 1.3 }}
                        >
                           {availabilityText}
                        </Typography>
                        <div style={{ width: "50%", minWidth: "60px" }}>
                           <ProgressBar
                              value={resource.available_percentage}
                              data-testid="availability-progress-bar"
                              style={{ marginTop: "5px", marginBottom: "0" }}
                           />
                        </div>
                     </div>
                     {showTags &&
                        resource.resource_tag_instances &&
                        resource.resource_tag_instances.length > 0 && (
                           <div className="bench-resource-tags">
                              {resource.resource_tag_instances.map((tag: any) => (
                                 <span
                                    key={`${resource.resource_id}-tag-${tag.id}`}
                                    className="bench-resource-tag"
                                    data-testid="badge"
                                    style={{
                                       backgroundColor: tag.color || "#f5f5f5",
                                    }}
                                 >
                                    {tag.abbreviation}
                                 </span>
                              ))}
                           </div>
                        )}
                  </Flex>
               </Flex>
            </div>
         </Card>
      </Tooltip>
   );
};
